import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { PiTrashBold } from 'react-icons/pi';

import { fetchMtrStructTree } from '#/api/imsMgmt/settings/mtrStruct/fetchTree';
import { cn } from '#/lib/utils';
import {
  Button,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import PlainRegisteredInput from '#/modules/commonComponents/form/PlainRegisteredInput';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import MtrPickerDialog from '#/modules/imsMgmt/settings/mtr/components/MtrPickerDialog';
import {
  mapMtrPickerRowToProductMaterial,
} from '#/modules/crmMgmt/product/helper/buildProductWritePayload';
import { resolveStructMainIdByCode } from '#/modules/crmMgmt/product/helper/resolveStructMainIdByCode';
import { mtrStructTreeQueryKey } from '#/utils/queryKeys';

const MATERIAL_LINE_COLS =
  '@lg/material-line:grid-cols-[minmax(0,1fr)_minmax(0,7rem)_minmax(0,1fr)_auto]';

const MATERIAL_LINE_HEADER_CLASS = cn(
  'sticky top-0 z-[1] hidden w-full gap-3 border-b border-gray-300 bg-gray-100 px-4 py-2',
  'text-xs font-medium text-gray-600',
  '@lg/material-line:grid',
  MATERIAL_LINE_COLS,
);

/** 僅 max-h 內捲；勿套 form-list--window（其含 min-height） */
const PRODUCT_LIST_MAX_HEIGHT_STYLE = {
  maxHeight: 'min(32rem, calc(100vh - var(--form-list-chrome)))',
};

const ProductMaterialRow = ({
  index,
  register,
  errors,
  line,
  onRemove,
}) => {
  const stackName = line.mtrName
    || line.mtrNo
    || (line.mtrId ? String(line.mtrId).slice(-8) : '');
  const stackCode = line.mtrNo || line.mtrId || '';

  return (
    <div className="border-b border-gray-200 px-4 py-2.5 last:border-b-0">
      <div
        className={cn(
          'grid grid-cols-1 items-center gap-3',
          MATERIAL_LINE_COLS,
        )}
      >
        <div className="min-w-0">
          <p className="@lg/material-line:hidden text-xs text-gray-500">物料</p>
          <MaterialNameStack name={stackName} code={stackCode} />
          <input type="hidden" {...register(`materials.${index}.mtrId`)} />
          <input type="hidden" {...register(`materials.${index}.mtrNo`)} />
          <input type="hidden" {...register(`materials.${index}.mtrName`)} />
        </div>
        <div className="min-w-0">
          <PlainRegisteredInput
            id={`materials.${index}.qtyPerProduct`}
            type="number"
            min={0.0001}
            step="any"
            register={register}
            errors={errors}
            mobileLabel="每產品用量"
            mobileLabelClassName="@lg/material-line:hidden"
            ariaLabel={`每產品用量 ${index + 1}`}
          />
        </div>
        <div className="min-w-0">
          <PlainRegisteredInput
            id={`materials.${index}.remark`}
            type="text"
            register={register}
            errors={errors}
            mobileLabel="行備註"
            mobileLabelClassName="@lg/material-line:hidden"
            ariaLabel={`行備註 ${index + 1}`}
          />
        </div>
        <div className="flex min-w-0 @lg/material-line:justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
            aria-label={`移除物料 ${index + 1}`}
            onClick={() => onRemove(index)}
          >
            <PiTrashBold className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

ProductMaterialRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.object.isRequired,
  onRemove: PropTypes.func.isRequired,
};

const ProductMaterialsEditor = ({
  control,
  register,
  errors,
  getValues,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKey, setPickerKey] = useState(0);

  const { fields, remove, replace } = useFieldArray({
    control,
    name: 'materials',
  });

  const watchedMaterials = useWatch({ control, name: 'materials', defaultValue: [] });

  const treeQuery = useQuery({
    queryKey: mtrStructTreeQueryKey(),
    queryFn: () => fetchMtrStructTree({ status: 'ACTIVE' }),
  });

  const structMainAId = useMemo(
    () => resolveStructMainIdByCode(treeQuery.data?.data ?? [], 'A'),
    [treeQuery.data?.data],
  );

  const pickerExtraFilters = useMemo(() => {
    if (!structMainAId) {
      return {};
    }
    return { mtrStructMainId: structMainAId };
  }, [structMainAId]);

  const linesForRender = useMemo(
    () => (fields ?? []).map((field, index) => {
      const watched = watchedMaterials?.[index] ?? {};
      return {
        ...field,
        ...watched,
        id: field.id,
      };
    }),
    [fields, watchedMaterials],
  );

  const initialSelectedRows = useMemo(
    () => linesForRender
      .filter((line) => line.mtrId)
      .map((line) => ({
        optionMtrId: line.mtrId,
        mtrNo: line.mtrNo,
        name: line.mtrName,
      })),
    [linesForRender],
  );

  function handleOpenPicker() {
    setPickerKey((prev) => prev + 1);
    setPickerOpen(true);
  }

  function handlePickerConfirm(selectedRows) {
    const existingById = new Map(
      (getValues('materials') || [])
        .filter((line) => line.mtrId)
        .map((line) => [line.mtrId, line]),
    );

    const nextLines = (selectedRows || [])
      .map((row) => {
        const id = row?.optionMtrId ?? row?.id;
        if (!id) {
          return null;
        }
        const kept = existingById.get(id);
        if (kept) {
          return kept;
        }
        return mapMtrPickerRowToProductMaterial(row);
      })
      .filter(Boolean);

    replace(nextLines);
  }

  return (
    <section className="form-section @container/form-section pb-5">
      <div className="form-section__header">
        <h2 className="form-block__title mb-0!">物料</h2>
        <Button type="button" size="sm" onClick={handleOpenPicker}>
          選料
        </Button>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        至少一筆；選料限啟用、一般料、主分類 A；同一物料不可重複。
      </p>

      {errors?.materials?.message ? (
        <p className="mt-2 text-sm text-red-500">{errors.materials.message}</p>
      ) : null}
      {errors?.materials?.root?.message ? (
        <p className="mt-2 text-sm text-red-500">{errors.materials.root.message}</p>
      ) : null}

      <div className="form-list mt-3" style={PRODUCT_LIST_MAX_HEIGHT_STYLE}>
        <div className="form-list__head">
          <p className="font-medium">已選擇物料</p>
          <p className="text-xs text-gray-500">
            {linesForRender.length}
            {' '}
            筆
          </p>
        </div>
        <div className={cn('form-list__body', '@container/material-line')}>
          {linesForRender.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              尚未選擇物料，請點「選料」新增。
            </p>
          ) : (
            <>
              <div className={MATERIAL_LINE_HEADER_CLASS} aria-hidden>
                <span className="min-w-0">物料</span>
                <span className="min-w-0">每產品用量</span>
                <span className="min-w-0">行備註</span>
                <span className="sr-only">操作</span>
              </div>
              {linesForRender.map((line, index) => (
                <ProductMaterialRow
                  key={line.id}
                  index={index}
                  register={register}
                  errors={errors}
                  line={line}
                  onRemove={remove}
                />
              ))}
            </>
          )}
        </div>
      </div>

      <MtrPickerDialog
        key={pickerKey}
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        initialSelectedRows={initialSelectedRows}
        onConfirm={handlePickerConfirm}
        extraFilters={pickerExtraFilters}
        title="請選擇產品物料"
        description="多選後確認；限 ACTIVE＋NORMAL＋主分類 A；已存在者不重複加入。"
      />
    </section>
  );
};

ProductMaterialsEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  getValues: PropTypes.func.isRequired,
};

export default ProductMaterialsEditor;
